import { useMemo } from "react";
import { getCoreRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";
import { Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";

import { WFPButton } from "@/components/ui/button";
import { DataTable } from "@/components/ui/data-table";
import type { AdministratorRow } from "../../schema";

interface AdministratorsTableProps {
  administrators: AdministratorRow[];
  onClickEdit: (administrator: AdministratorRow) => void;
  onClickDelete: (administrator: AdministratorRow) => void;
}

export const AdministratorsTable = ({
  administrators,
  onClickEdit,
  onClickDelete,
}: AdministratorsTableProps) => {
  const { t } = useTranslation();

  const columns = useMemo<ColumnDef<AdministratorRow>[]>(
    () => [
      {
        id: "last_name",
        accessorKey: "last_name",
        header: t("column.lastName"),
        meta: { cellClassName: "px-3 min-w-40", headerClassName: "px-3 min-w-40" },
      },
      {
        id: "email",
        accessorKey: "email",
        header: t("fieldName.email"),
        meta: { cellClassName: "px-3 min-w-52", headerClassName: "px-3 min-w-52" },
      },
      {
        id: "position",
        accessorKey: "position",
        header: t("column.position"),
        meta: { cellClassName: "px-3 min-w-40", headerClassName: "px-3 min-w-40" },
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => (
          <div className="flex justify-end gap-1">
            <WFPButton
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("common.edit")}
              onClick={() => onClickEdit(row.original)}
            >
              <Pencil className="size-4" />
            </WFPButton>
            <WFPButton
              type="button"
              variant="ghost"
              size="icon"
              aria-label={t("common.delete")}
              onClick={() => onClickDelete(row.original)}
            >
              <Trash2 className="size-4" />
            </WFPButton>
          </div>
        ),
        meta: { cellClassName: "w-0 px-3", headerClassName: "w-0 px-3" },
      },
    ],
    [onClickDelete, onClickEdit, t]
  );

  const table = useReactTable({
    columns,
    data: administrators,
    getCoreRowModel: getCoreRowModel(),
    getRowId: row => row.id,
  });

  return (
    <DataTable
      table={table}
      isLoading={false}
      emptyMessage={t("aggregatorDetails.administratorModal.noAdminsAdded")}
    />
  );
};
